import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { marketplaceApi } from '../services/api';
import type { ProviderRequest } from '../types';

export type ProviderType = 'lawyer' | 'inspector';

/**
 * The lawyer's or inspector's own requests (marketplace), with the answer
 * ("Acceptera" / "Avslå") the start page gives from "Att göra nu".
 */
export function useProviderRequests(providerType: ProviderType) {
  const { t } = useTranslation();
  const [requests, setRequests] = useState<ProviderRequest[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setRequests(await marketplaceApi.getMyRequests());
      setError('');
    } catch {
      setError(t('providerRequests.loadError', 'Kunde inte ladda förfrågningar'));
    } finally {
      setLoaded(true);
    }
  }, [t]);

  useEffect(() => { void load(); }, [load]);

  const mine = useMemo(() => requests.filter(request => request.providerType === providerType), [requests, providerType]);
  const pending = useMemo(() => mine.filter(request => request.status === 'pending'), [mine]);

  /** Answers a request; true when it went through. */
  const respond = async (request: ProviderRequest, action: 'accepted' | 'declined') => {
    setBusyId(request.id);
    setError('');
    try {
      await marketplaceApi.respondToRequest(request.id, { action });
      await load();
      return true;
    } catch {
      setError(t('providerRequests.respondError', 'Kunde inte registrera svaret'));
      return false;
    } finally {
      setBusyId(null);
    }
  };

  return { requests: mine, pending, loaded, busyId, error, respond };
}
